import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { ArrowUpRight, Plus } from 'lucide-react';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { useAuthStore } from '../stores/authStore';
import api from '../lib/axios';
import { euro, dateFmt } from '../lib/format';
import { useModuleVisibility } from '../lib/useModuleVisibility';

const pillClass = (status: string) =>
  status === 'DRAFT' ? 'pill pill-draft' : status === 'POSTED' ? 'pill pill-posted' : 'pill pill-neutral';

type ChartRow = { name: string; hijri: string; income: number; expenses: number };

// X-axis tick that stacks the Hijri month under the Gregorian month when the feature is enabled.
function MonthTick(props: any) {
  const { x, y, payload, data } = props as {
    x: number;
    y: number;
    payload: { value: string };
    data: ChartRow[];
  };
  const row = data.find((d) => d.name === payload.value);
  return (
    <g transform={`translate(${x},${y})`}>
      <text x={0} y={0} dy={12} textAnchor="middle" fontSize={12} fill="var(--ink-soft, #6b7280)">
        {payload.value}
      </text>
      {row?.hijri && (
        <text x={0} y={0} dy={26} textAnchor="middle" fontSize={10} fill="var(--gold, #D98A3D)">
          {row.hijri}
        </text>
      )}
    </g>
  );
}

type MonthResult = { income: number; expenses: number; result: number };
type MonthPoint = { month: string; income: number; expenses: number; result: number };
type DashboardData = {
  monthly: MonthPoint[];
  currentMonth: MonthResult;
  previousMonth: MonthResult;
  year: { income: number; expenses: number; result: number };
  cheques: { toDepositCount: number; toDepositAmount: number; totalCount: number };
};

const monthLabel = (ym: string) => {
  const [y, m] = ym.split('-');
  const d = new Date(Number(y), Number(m) - 1, 1);
  return d.toLocaleDateString('fr-FR', { month: 'short' });
};

// Dominant Hijri (Umm al-Qura) month for a Gregorian year-month, via the browser Intl calendar.
const hijriLabel = (ym: string) => {
  const [y, m] = ym.split('-');
  const d = new Date(Number(y), Number(m) - 1, 15);
  try {
    return new Intl.DateTimeFormat('fr-u-ca-islamic-umalqura', { month: 'long' }).format(d);
  } catch {
    return '';
  }
};

export default function Dashboard() {
  const { t } = useTranslation();
  const tenant = useAuthStore((s) => s.tenant);
  const { isHidden } = useModuleVisibility();

  const balance = useQuery({
    queryKey: ['cash-balance'],
    queryFn: () => api.get('/cash/balance').then((r) => r.data),
  });

  const dashboard = useQuery({
    queryKey: ['dashboard-kpis'],
    queryFn: () =>
      api.get<DashboardData>('/reports/dashboard').then((r) => r.data),
    refetchInterval: 30_000,
    staleTime: 25_000,
  });

  const entries = useQuery({
    queryKey: ['recent-entries'],
    queryFn: () =>
      api.get('/entries', { params: { page: 0, size: 6 } }).then((r) => r.data),
  });

  const settings = useQuery({
    queryKey: ['settings'],
    queryFn: () => api.get('/settings').then((r) => r.data),
    staleTime: 5 * 60_000,
  });
  const cashEnabled = settings.data?.cashEnabled !== false;
  const monthMode = settings.data?.dashboardMonthMode === 'PREVIOUS' ? 'PREVIOUS' : 'CURRENT';
  const hijriEnabled = !!settings.data?.hijriCalendarEnabled;

  const rows = entries.data?.content ?? entries.data ?? [];
  const kpis = dashboard.data;
  const num = (v: number | string | undefined) => (typeof v === 'string' ? parseFloat(v) : v ?? 0);
  const monthData = monthMode === 'PREVIOUS' ? kpis?.previousMonth : kpis?.currentMonth;
  const chartData = (kpis?.monthly ?? []).map((p) => ({
    name: monthLabel(p.month),
    hijri: hijriEnabled ? hijriLabel(p.month) : '',
    income: num(p.income),
    expenses: num(p.expenses),
  }));

  return (
    <div>
      <header className="reveal flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="eyebrow">{tenant?.name ?? t('common.appName')}</p>
          <h1 className="page-title mt-1">{t('dashboard.title')}</h1>
        </div>
        <Link to="/entries" className="btn btn-primary">
          <Plus size={16} /> {t('entries.new')}
        </Link>
      </header>

      <section className="mt-8 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
        {/* Cash balance */}
        {cashEnabled && !isHidden('cash') && (
        <div className="reveal card flex flex-col justify-between p-6" style={{ animationDelay: '40ms' }}>
          <p className="eyebrow">{t('dashboard.cashBalance')}</p>
          <p className="mt-3 font-display text-3xl font-semibold tracking-tight text-ink tnum">
            {balance.isLoading ? '-' : euro(balance.data?.balance)}
          </p>
          <div className="mt-4 border-t border-line pt-3">
            <Link to="/cash" className="inline-flex items-center gap-1 text-sm font-medium text-brand hover:text-brand-strong">
              {t('nav.cash')} <ArrowUpRight size={15} />
            </Link>
          </div>
        </div>
        )}

        {/* Month result (current or previous month, per tenant setting) */}
        <div className="reveal card flex flex-col justify-between p-6" style={{ animationDelay: '80ms' }}>
          <p className="eyebrow">{monthMode === 'PREVIOUS' ? t('dashboard.prevMonthResult') : t('dashboard.monthResult')}</p>
          <p className="mt-3 font-display text-3xl font-semibold tracking-tight text-ink tnum">
            {monthData ? euro(num(monthData.result)) : '-'}
          </p>
          <div className="mt-4 flex items-center justify-between border-t border-line pt-3 text-xs text-ink-soft">
            <span>{t('dashboard.monthIncome')}: {monthData ? euro(num(monthData.income)) : '-'}</span>
            <span>{t('dashboard.monthExpenses')}: {monthData ? euro(num(monthData.expenses)) : '-'}</span>
          </div>
        </div>

        {/* Year result */}
        <div className="reveal card flex flex-col justify-between p-6" style={{ animationDelay: '100ms' }}>
          <p className="eyebrow">{t('dashboard.yearResult')}</p>
          <p className={`mt-3 font-display text-3xl font-semibold tracking-tight tnum ${kpis && num(kpis.year.result) < 0 ? 'text-expense' : 'text-income'}`}>
            {kpis ? euro(num(kpis.year.result)) : '-'}
          </p>
          <div className="mt-4 flex items-center justify-between border-t border-line pt-3 text-xs text-ink-soft">
            <span>{t('dashboard.income')}: {kpis ? euro(num(kpis.year.income)) : '-'}</span>
            <span>{t('dashboard.expenses')}: {kpis ? euro(num(kpis.year.expenses)) : '-'}</span>
          </div>
        </div>

        {/* Cheques to deposit */}
        {!isHidden('checks') && (
        <div className="reveal card flex flex-col justify-between p-6" style={{ animationDelay: '120ms' }}>
          <p className="eyebrow">{t('dashboard.chequesToDeposit')}</p>
          <p className="mt-3 font-display text-3xl font-semibold tracking-tight text-ink tnum">
            {kpis ? kpis.cheques.toDepositCount : '-'}
          </p>
          <div className="mt-4 flex items-center justify-between border-t border-line pt-3">
            <span className="text-sm text-ink-soft tnum">{kpis ? euro(num(kpis.cheques.toDepositAmount)) : '-'}</span>
            <Link to="/checks" className="inline-flex items-center gap-1 text-sm font-medium text-brand hover:text-brand-strong">
              {t('nav.checks')} <ArrowUpRight size={15} />
            </Link>
          </div>
        </div>
        )}
      </section>

      <section className="mt-5 grid gap-5 lg:grid-cols-3">
        {/* Income vs expenses chart */}
        <div className="reveal card p-6 lg:col-span-2" style={{ animationDelay: '200ms' }}>
          <div className="flex flex-wrap items-center justify-between gap-3">
            <h2 className="font-display text-lg font-semibold">{t('dashboard.monthlyTitle')}</h2>
          </div>
          <div className="mt-4 h-72">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={chartData} margin={{ top: 8, right: 8, left: 8, bottom: hijriEnabled ? 14 : 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--line, #e5e7eb)" vertical={false} />
                <XAxis
                  dataKey="name"
                  tickLine={false}
                  axisLine={false}
                  fontSize={12}
                  height={hijriEnabled ? 40 : 30}
                  tick={hijriEnabled ? <MonthTick data={chartData} /> : undefined}
                />
                <YAxis tickLine={false} axisLine={false} fontSize={12} width={70}
                  tickFormatter={(v) => euro(v as number)} />
                <Tooltip formatter={(v) => euro(v as number)} />
                <Legend />
                <Bar dataKey="income" name={t('dashboard.income')} fill="#1A6B72" radius={[4, 4, 0, 0]} />
                <Bar dataKey="expenses" name={t('dashboard.expenses')} fill="#D98A3D" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Recent entries */}
        <div className="reveal card p-0" style={{ animationDelay: '240ms' }}>
          <div className="flex items-center justify-between px-6 py-4">
            <h2 className="font-display text-lg font-semibold">
              {t('dashboard.latestEntries')}
            </h2>
            <Link
              to="/entries"
              className="inline-flex items-center gap-1 text-sm font-medium text-brand hover:text-brand-strong"
            >
              {t('nav.entries')} <ArrowUpRight size={15} />
            </Link>
          </div>

          {rows.length > 0 ? (
            <div className="px-3 pb-3">
              <table className="table">
                <thead>
                  <tr>
                    <th>{t('field.date')}</th>
                    <th>{t('field.label')}</th>
                    <th className="num">{t('field.amount')}</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((e: any) => (
                    <tr key={e.id}>
                      <td data-label={t('field.date')} className="whitespace-nowrap text-ink-soft">{dateFmt(e.entryDate)}</td>
                      <td data-label={t('field.label')} className="font-medium">
                        {e.label}
                        <span className={`ml-2 ${pillClass(e.status)}`}>
                          {t('entries.status.' + e.status) as string}
                        </span>
                      </td>
                      <td data-label={t('field.amount')} className="num">{euro(e.totalDebit)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="px-6 pb-10 pt-4 text-center">
              <p className="font-display text-base text-ink-soft">
                {t('dashboard.noEntries')}
              </p>
              <Link to="/entries" className="btn btn-secondary mt-4">
                <Plus size={16} /> {t('entries.new')}
              </Link>
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
